import { useEffect, useState } from "react";

import dayjs from "dayjs";
import "dayjs/locale/ko";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useNavigate } from "react-router-dom";

import { postReservationsByState, fetchVehicleReservations } from "@/apis/reservation";
import Layout from "@/components/Layout";
import { HOME_MAIN_URL } from "@/constants/routes";
import useViewMode from "@/hooks/useViewMode";
import type { ReservationByState } from "@/types/reservation";

dayjs.locale("ko");

// ============================================
// Types
// ============================================

interface MonthData {
  month: string; // "1월", "2월", ...
  count: number;
}

interface PlaceRankData {
  name: string;
  count: number;
}

interface StatusData {
  name: string;
  value: number;
  color: string;
}

// ============================================
// Constants
// ============================================

const PRIMARY_COLOR = "#06367A";
const STATUS_COLORS: Record<string, string> = {
  approve: "#22c55e",
  request: "#f59e0b",
  reject: "#ef4444",
  cancel: "#9ca3af",
};

const STATUS_KO: Record<string, string> = {
  approve: "승인",
  request: "대기",
  reject: "반려",
  cancel: "취소",
};

// ============================================
// Helper
// ============================================

function getLastNMonths(n: number, base: dayjs.Dayjs): dayjs.Dayjs[] {
  const months: dayjs.Dayjs[] = [];
  for (let i = n - 1; i >= 0; i--) {
    months.push(base.subtract(i, "month"));
  }
  return months;
}


// ============================================
// Sub-components
// ============================================

const SummaryCard = ({
  title,
  value,
  sub,
  icon,
}: {
  title: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
}) => (
  <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-500">{title}</span>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
    </div>
    <p className="text-3xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-400">{sub}</p>}
  </div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg font-semibold text-gray-800">{children}</h2>
);

const ChartCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
    <SectionTitle>{title}</SectionTitle>
    <div className="mt-4">{children}</div>
  </div>
);

const EmptyState = () => (
  <div className="flex h-40 items-center justify-center text-sm text-gray-400">
    데이터 없음
  </div>
);

// Custom tooltip for bar charts
const CustomBarTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { value: number }[];
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border border-gray-100 bg-white px-3 py-2 shadow-md text-sm">
        <p className="font-medium text-gray-700">{label}</p>
        <p className="text-primary">{payload[0].value}건</p>
      </div>
    );
  }
  return null;
};

// ============================================
// Month Selector
// ============================================

interface MonthSelectorProps {
  selected: dayjs.Dayjs;
  onChange: (d: dayjs.Dayjs) => void;
}

const MonthSelector = ({ selected, onChange }: MonthSelectorProps) => {
  const prevMonth = () => onChange(selected.subtract(1, "month"));
  const nextMonth = () => {
    const next = selected.add(1, "month");
    if (next.isAfter(dayjs(), "month")) return;
    onChange(next);
  };

  const isCurrentMonth = selected.isSame(dayjs(), "month");

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={prevMonth}
        className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-600 transition hover:bg-gray-50 active:scale-95"
      >
        &lt;
      </button>
      <span className="min-w-[88px] text-center text-base font-semibold text-gray-800">
        {selected.format("YYYY년 M월")}
      </span>
      <button
        type="button"
        onClick={nextMonth}
        disabled={isCurrentMonth}
        className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-600 transition hover:bg-gray-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
      >
        &gt;
      </button>
    </div>
  );
};

// ============================================
// Place Ranking Section (with filters)
// ============================================

const PlaceRankingSection = ({ selectedMonth }: { selectedMonth: dayjs.Dayjs }) => {
  const [period, setPeriod] = useState<"month" | "year">("month");
  const [order, setOrder] = useState<"desc" | "asc">("desc");
  const [ranking, setRanking] = useState<PlaceRankData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRanking();
  }, [selectedMonth, period, order]);

  const loadRanking = async () => {
    setLoading(true);
    try {
      let fromDate: string;
      let toDate: string;

      if (period === "month") {
        fromDate = selectedMonth.startOf("month").format("YYYYMMDD");
        toDate = selectedMonth.endOf("month").format("YYYYMMDD");
      } else {
        fromDate = selectedMonth.startOf("year").format("YYYYMMDD");
        toDate = selectedMonth.endOf("year").format("YYYYMMDD");
      }

      const res = await postReservationsByState(0, 500, "all", "all", fromDate, toDate);
      const content: ReservationByState[] = res?.content ?? [];

      const countMap: Record<string, { name: string; count: number }> = {};
      content.forEach((r) => {
        if (!countMap[r.plcCd]) {
          countMap[r.plcCd] = { name: r.plcNm || r.plcCd, count: 0 };
        }
        countMap[r.plcCd].count += 1;
      });

      const sorted = Object.values(countMap)
        .sort((a, b) => order === "desc" ? b.count - a.count : a.count - b.count)
        .slice(0, 10);

      setRanking(sorted);
    } catch {
      setRanking([]);
    } finally {
      setLoading(false);
    }
  };

  const periodLabel = period === "month"
    ? selectedMonth.format("YYYY년 M월")
    : selectedMonth.format("YYYY년");

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>장소 사용 순위</SectionTitle>
        <div className="flex gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setPeriod("month")}
              className={`px-3 py-1.5 transition-colors ${period === "month" ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            >
              월간
            </button>
            <button
              type="button"
              onClick={() => setPeriod("year")}
              className={`px-3 py-1.5 transition-colors ${period === "year" ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            >
              연간
            </button>
          </div>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setOrder("desc")}
              className={`px-3 py-1.5 transition-colors ${order === "desc" ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            >
              많은 순
            </button>
            <button
              type="button"
              onClick={() => setOrder("asc")}
              className={`px-3 py-1.5 transition-colors ${order === "asc" ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            >
              적은 순
            </button>
          </div>
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-400">{periodLabel} 기준 · TOP 10</p>
      <div className="mt-4">
        {loading ? (
          <div className="flex h-[360px] items-center justify-center text-sm text-gray-400">로딩 중...</div>
        ) : ranking.length === 0 ? (
          <div className="flex h-[360px] items-center justify-center text-sm text-gray-400">데이터 없음</div>
        ) : (
          <div className="h-[360px] transition-all duration-300">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={ranking}
                margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
              >
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={90}
                  tick={{ fontSize: 11, fill: "#374151" }}
                />
                <Tooltip content={<CustomBarTooltip />} />
                <Bar dataKey="count" fill={PRIMARY_COLOR} radius={[0, 4, 4, 0]} animationDuration={500} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// Main Chart Page
// ============================================

const ChartPage = () => {
  const navigate = useNavigate();
  const { isRealAdmin } = useViewMode();

  // Redirect non-admins
  useEffect(() => {
    if (!isRealAdmin) {
      navigate(HOME_MAIN_URL, { replace: true });
    }
  }, [isRealAdmin, navigate]);

  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs().startOf("month"));
  const [loading, setLoading] = useState(true);

  // Summary state
  const [thisMonthPlaceCount, setThisMonthPlaceCount] = useState(0);
  const [thisMonthVehicleCount, setThisMonthVehicleCount] = useState(0);
  const [approvalRate, setApprovalRate] = useState<number | null>(null);
  const [topPlace, setTopPlace] = useState<string>("-");

  // Trend charts state
  const [placeTrend, setPlaceTrend] = useState<MonthData[]>([]);
  const [vehicleTrend, setVehicleTrend] = useState<MonthData[]>([]);


  // Status distribution state
  const [statusDist, setStatusDist] = useState<StatusData[]>([]);

  useEffect(() => {
    if (!isRealAdmin) return;
    loadAllData();
  }, [selectedMonth, isRealAdmin]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const last6 = getLastNMonths(6, selectedMonth);
      const sixMonthsAgoFrom = last6[0].startOf("month").format("YYYYMMDD");
      const toDate = selectedMonth.endOf("month").format("YYYYMMDD");
      const thisMonthFrom = selectedMonth.startOf("month").format("YYYYMMDD");

      // 차량: 연도가 걸치면 2번, 아니면 1번 호출
      const firstYear = last6[0].year().toString();
      const lastYear = selectedMonth.year().toString();
      const vehicleYears = firstYear === lastYear ? [firstYear] : [firstYear, lastYear];

      const [placeAllRes, ...vehicleByYear] = await Promise.all([
        // 6개월치 장소 예약 전체 (최대 500건)
        postReservationsByState(0, 500, "all", "all", sixMonthsAgoFrom, toDate),
        // 차량: 연도 단위로 1~2번 호출
        ...vehicleYears.map((y) => fetchVehicleReservations(y, "all")),
      ]);

      // 차량 전체 데이터 합치기
      const allVehicleData = vehicleByYear.flat();

      const allPlaceData: ReservationByState[] = placeAllRes?.content ?? [];

      // ---------- 이번 달 데이터 필터 ----------
      const thisMonthData = allPlaceData.filter(
        (r) => r.rsvtDt >= thisMonthFrom && r.rsvtDt <= toDate,
      );

      // ---------- Summary ----------
      const thisMonthYM = `${selectedMonth.format("YYYY")}-${selectedMonth.format("MM")}`;
      const vehicleThisMonthData = allVehicleData.filter(
        (r) => r.reservationDate?.startsWith(thisMonthYM),
      );
      setThisMonthPlaceCount(thisMonthData.length);
      setThisMonthVehicleCount(vehicleThisMonthData.length);

      // ---------- Status Distribution (이번 달) ----------
      const statusCount: Record<string, number> = { approve: 0, request: 0, reject: 0, cancel: 0 };
      thisMonthData.forEach((r) => {
        if (statusCount[r.sttCd] !== undefined) statusCount[r.sttCd]++;
      });
      const totalForRate = Object.values(statusCount).reduce((a, b) => a + b, 0);
      setApprovalRate(totalForRate > 0 ? Math.round((statusCount.approve / totalForRate) * 100) : null);
      setStatusDist([
        { name: STATUS_KO.approve, value: statusCount.approve, color: STATUS_COLORS.approve },
        { name: STATUS_KO.request, value: statusCount.request, color: STATUS_COLORS.request },
        { name: STATUS_KO.reject, value: statusCount.reject, color: STATUS_COLORS.reject },
        { name: STATUS_KO.cancel, value: statusCount.cancel, color: STATUS_COLORS.cancel },
      ]);

      // ---------- Top Place ----------
      const placeCountMap: Record<string, { name: string; count: number }> = {};
      thisMonthData.forEach((r) => {
        if (!placeCountMap[r.plcCd]) {
          placeCountMap[r.plcCd] = { name: r.plcNm || r.plcCd, count: 0 };
        }
        placeCountMap[r.plcCd].count++;
      });
      const topPlaceSorted = Object.values(placeCountMap).sort((a, b) => b.count - a.count);
      setTopPlace(topPlaceSorted.length > 0 ? topPlaceSorted[0].name : "-");

      // ---------- Trend: Place (6개월) ----------
      setPlaceTrend(last6.map((m) => {
        const mFrom = m.startOf("month").format("YYYYMMDD");
        const mTo = m.endOf("month").format("YYYYMMDD");
        const count = allPlaceData.filter((r) => r.rsvtDt >= mFrom && r.rsvtDt <= mTo).length;
        return { month: `${m.month() + 1}월`, count };
      }));

      // ---------- Trend: Vehicle (6개월) ----------
      setVehicleTrend(last6.map((m) => {
        const ym = `${m.format("YYYY")}-${m.format("MM")}`;
        const count = allVehicleData.filter((r) => r.reservationDate?.startsWith(ym)).length;
        return { month: `${m.month() + 1}월`, count };
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isRealAdmin) return null;

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        {/* Header row with month selector */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            선택한 월을 기준으로 통계를 표시합니다
          </p>
          <MonthSelector selected={selectedMonth} onChange={setSelectedMonth} />
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center text-gray-400">
            로딩 중...
          </div>
        ) : (
          <>
            {/* ======================================
                Section 1: Summary Cards
            ====================================== */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <SummaryCard
                title="이번 달 장소 예약 수"
                value={thisMonthPlaceCount}
                sub="건의 장소 예약"
                icon={
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                }
              />
              <SummaryCard
                title="이번 달 차량 예약 수"
                value={thisMonthVehicleCount}
                sub="건의 차량 예약"
                icon={
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                }
              />
              <SummaryCard
                title="이번 달 승인율"
                value={approvalRate !== null ? `${approvalRate}%` : "-"}
                sub="장소 예약 기준"
                icon={
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
              <SummaryCard
                title="가장 많이 예약된 장소"
                value={topPlace}
                sub="이번 달 기준"
                icon={
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                }
              />
            </div>

            {/* ======================================
                Section 2 & 3: Trend Charts (2 columns)
            ====================================== */}
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Place trend */}
              <ChartCard title="장소 예약 추이 (최근 6개월)">
                {placeTrend.every((d) => d.count === 0) ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={placeTrend} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6b7280" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                      <Tooltip content={<CustomBarTooltip />} />
                      <Bar dataKey="count" fill={PRIMARY_COLOR} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              {/* Vehicle trend */}
              <ChartCard title="차량 예약 추이 (최근 6개월)">
                {vehicleTrend.every((d) => d.count === 0) ? (
                  <EmptyState />
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={vehicleTrend} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6b7280" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                      <Tooltip content={<CustomBarTooltip />} />
                      <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            {/* ======================================
                Section 4 & 5: Ranking + Pie (2 columns)
            ====================================== */}
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Place usage ranking */}
              <PlaceRankingSection selectedMonth={selectedMonth} />

              {/* Status distribution pie */}
              <ChartCard title="예약 상태 분포 (이번 달 장소 예약)">
                {statusDist.every((d) => d.value === 0) ? (
                  <EmptyState />
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={statusDist}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          innerRadius={40}
                          paddingAngle={2}
                        >
                          {statusDist.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [`${value}건`]}
                          contentStyle={{ fontSize: 13, borderRadius: 8 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>

                    {/* Legend */}
                    <div className="flex flex-wrap justify-center gap-3">
                      {statusDist.map((entry) => (
                        <div key={entry.name} className="flex items-center gap-1.5 text-sm text-gray-600">
                          <span
                            className="inline-block h-3 w-3 rounded-full"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span>{entry.name}</span>
                          <span className="font-medium text-gray-800">{entry.value}건</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </ChartCard>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default ChartPage;
